// src/config/roles.js
// verticalstack — Single source of truth for role + vertical definitions
// Used by: Shell, ShellNav, WorkspacePicker, useMembership, future onboarding flows
//
// S31 LAYER 1 (Apr 27, 2026):
//   COMPLETE REWRITE for sandbox-schema migration.
//   - Replaces 14 PascalCase roles with 9 lowercase sandbox roles.
//   - clientMembers.role is now an ARRAY (per-workspace role list per AS-E),
//     not a single string. Helpers operate on arrays accordingly.
//   - ClientAdmin retired — admin authority comes from platformAdmins/{uid}
//     doc existence per new firestore.rules (AS-I).
//   - Guest and Auditor deferred (AS-H) — no sandbox equivalent in 9-role set.
//     Treated as unknown roles by getRoleLabel; not in any vertical's allow list.
//   - VERTICAL_LABELS / VERTICAL_DESCRIPTIONS retained; VERTICAL_ROLES rewritten
//     in lowercase enum.
//
// History (preserved for context):
//   S16: Added 6 CM vertical roles (CP2)
//   S16B: Added helper functions and VERTICAL_LABELS map (AS-204)
//   S16C: Firestore rules canonicalized to PascalCase (AS-209) — caused AS-214
//   S17:  AS-214 resolved — PascalCase canonical across the app
//   S31:  Sandbox-schema migration — lowercase roles, array-based memberships

// ── Role value constants (canonical lowercase per sandbox schema) ──────────

export const ROLES = {
  OWNER_PM:   'owner_pm',     // Owner who self-manages (Chris's role in all 3 LLCs)
  OWNER_ONLY: 'owner_only',   // Owner who delegates to a PM
  PM_ONLY:    'pm_only',      // Professional PM who manages but does not own
  TENANT:     'tenant',       // LTR tenant
  VENDOR:     'vendor',       // Outside vendor (e.g., APS Management)
  SUB:        'sub',          // Subcontractor on work orders
  DEVELOPER:  'developer',    // CM vertical: project owner / developer
  CM:         'cm',           // CM vertical: construction manager
  INVESTOR:   'investor',     // Capital partner / LP-style role
};

// All valid role identifiers as a flat array — use for enum validation
export const ALL_ROLES = Object.values(ROLES);

// ── UI display labels ─────────────────────────────────────────────────────

export const ROLE_LABELS = {
  owner_pm:   'Owner / Property Manager',
  owner_only: 'Owner',
  pm_only:    'Property Manager',
  tenant:     'Tenant',
  vendor:     'Vendor',
  sub:        'Subcontractor',
  developer:  'Developer',
  cm:         'Construction Manager',
  investor:   'Investor',
};

// ── Vertical type labels for UI display ────────────────────────────────────

export const VERTICAL_LABELS = {
  property_management:     'Property Management',
  ltr:                     'Long-Term Rental',
  str:                     'Short-Term Rental',
  commercial:              'Commercial Real Estate',
  rv_park:                 'RV Park / Campground',
  hoa:                     'HOA Management',
  construction_management: 'Construction Management',
};

// Vertical descriptions — used by onboarding flows when those land in Layer 2+
export const VERTICAL_DESCRIPTIONS = {
  property_management:
    'General property management — mixed STR + LTR portfolio with flexible scope.',
  ltr:
    'Long-term residential rentals — single family, multi-family, leases.',
  str:
    'Short-term rentals — Airbnb, VRBO, vacation properties.',
  commercial:
    'Commercial real estate — office, retail, NNN leases.',
  rv_park:
    'RV parks and campgrounds — site-based bookings.',
  hoa:
    'HOA management — dues, violations, board portal.',
  construction_management:
    'Construction management — RFQs, bids, RFIs, punch lists.',
};

// ── Role groupings for permission checks ───────────────────────────────────
// NOTE: Admin authority is NOT a role grouping in the new model.
//       Admin = platformAdmins/{uid} doc exists. See isPlatformAdmin in useAuth.

// Roles that have property-management authority within a workspace
// (can dispatch work orders, manage tenants, see financials)
export const MANAGER_ROLES = ['owner_pm', 'pm_only'];

// Roles that hold ownership equity in a workspace's parcels
export const OWNER_ROLES = ['owner_pm', 'owner_only', 'investor'];

// CM vertical role group
export const CM_ROLES = ['developer', 'cm'];

// ── Vertical type → allowed roles mapping ──────────────────────────────────
// Lowercase enums per sandbox schema. Guest and Auditor (AS-H) are deferred —
// not present in any vertical's allow list in Layer 1.

export const VERTICAL_ROLES = {
  property_management: ['owner_pm', 'owner_only', 'pm_only', 'tenant', 'vendor', 'sub', 'investor'],
  str:                 ['owner_pm', 'owner_only', 'pm_only', 'vendor', 'sub', 'investor'],
  ltr:                 ['owner_pm', 'owner_only', 'pm_only', 'tenant', 'vendor', 'sub', 'investor'],
  commercial:          ['owner_pm', 'owner_only', 'pm_only', 'tenant', 'vendor', 'sub', 'investor'],
  rv_park:             ['owner_pm', 'owner_only', 'pm_only', 'vendor', 'sub', 'investor'],
  hoa:                 ['owner_pm', 'pm_only', 'vendor', 'sub'],
  construction_management: ['developer', 'cm', 'sub', 'vendor', 'investor'],
};

// ── Helper functions ───────────────────────────────────────────────────────

/**
 * Returns human-readable label for a role identifier.
 * Falls back to the raw role string if not in ROLE_LABELS.
 */
export function getRoleLabel(role) {
  if (!role) return 'Unknown';
  return ROLE_LABELS[role] || role;
}

/**
 * Returns a comma-separated label list for an array of roles.
 * Per AS-E, clientMembers.role is an array; use this when displaying
 * a member's full role set in a workspace.
 *
 * Example: ['owner_pm'] → 'Owner / Property Manager'
 *          ['owner_pm', 'investor'] → 'Owner / Property Manager, Investor'
 */
export function getRolesLabel(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return 'Unknown';
  return roles.map(getRoleLabel).join(', ');
}

/**
 * Returns human-readable label for a vertical identifier.
 * Falls back to the raw vertical string if not in VERTICAL_LABELS.
 */
export function getVerticalLabel(vertical) {
  if (!vertical) return 'Unknown';
  return VERTICAL_LABELS[vertical] || vertical;
}

/**
 * True if the membership has manager authority in the workspace.
 * Accepts the roles ARRAY from clientMembers, not a single string.
 *
 * Example: hasManagerRole(['owner_pm']) → true
 *          hasManagerRole(['tenant'])   → false
 */
export function hasManagerRole(roles) {
  if (!Array.isArray(roles)) return false;
  return roles.some(r => MANAGER_ROLES.includes(r));
}

/**
 * True if the membership has ownership equity in the workspace.
 * Accepts the roles ARRAY from clientMembers, not a single string.
 */
export function hasOwnerRole(roles) {
  if (!Array.isArray(roles)) return false;
  return roles.some(r => OWNER_ROLES.includes(r));
}

/**
 * True if the role identifier is a valid sandbox role.
 * Useful for input validation when invitations / role updates land in Layer 2+.
 */
export function isValidRole(role) {
  return ALL_ROLES.includes(role);
}

/**
 * Returns array of role identifiers allowed for a given vertical.
 * Returns empty array if vertical unknown.
 */
export function getAllowedRolesForVertical(vertical) {
  return VERTICAL_ROLES[vertical] || [];
}
